// =============================================================================
// Shared Constants — PTC Tax Lifecycle Web App
// =============================================================================
// Runtime constants extracted from the codebase to reduce typo-related bugs.
// Types remain in @/types; these are values safe to use in comparisons, API
// calls, and configuration.
// =============================================================================

// ---------------------------------------------------------------------------
// API Paths
// ---------------------------------------------------------------------------
// Base path segments used by services/api.ts. Each constant maps to the
// leading segment of an endpoint group.

export const API_PATHS = {
  TAX_RETURNS: '/tax-returns',
  DASHBOARD: '/dashboard',
  SYSTEM_USERS: '/system-users',
  UPLOAD_LINKS: '/upload-links',
  APPOINTMENTS: '/appointments',
  OUTREACH: '/outreach',
  DRAKE: '/drake',
  BOOKKEEPING: '/bookkeeping',
  NOTIFICATIONS: '/notifications',
  INQUIRIES: '/inquiries',
  OPT_OUTS: '/opt-outs',
  INHOUSE_CLIENTS: '/inhouse-clients',
  INVOICES: '/invoices',
  PORTAL_UPLOADS: '/portal-uploads',
  DATAVERSE: '/dataverse',
} as const;

// ---------------------------------------------------------------------------
// Tax Return Status
// ---------------------------------------------------------------------------

export const TAX_RETURN_STATUS = {
  SCHEDULED: 'scheduled',
  PENDING_RESOLUTION: 'pending_resolution',
  DOCUMENTS_PENDING: 'documents_pending',
  IN_PREPARATION: 'in_preparation',
  IN_REVIEW: 'in_review',
  NEEDS_CORRECTIONS: 'needs_corrections',
  READY_FOR_SIGNATURE: 'ready_for_signature',
  WAITING_FOR_PAYMENT: 'waiting_for_payment',
  READY_FOR_EFILE: 'ready_for_efile',
  E_FILED: 'e_filed',
  COMPLETED: 'completed',
  INACTIVE: 'inactive',
} as const;

/** Ordered list of statuses reflecting the typical workflow progression. */
export const TAX_RETURN_STATUS_ORDER: ReadonlyArray<
  (typeof TAX_RETURN_STATUS)[keyof typeof TAX_RETURN_STATUS]
> = [
  TAX_RETURN_STATUS.SCHEDULED,
  TAX_RETURN_STATUS.PENDING_RESOLUTION,
  TAX_RETURN_STATUS.DOCUMENTS_PENDING,
  TAX_RETURN_STATUS.IN_PREPARATION,
  TAX_RETURN_STATUS.IN_REVIEW,
  TAX_RETURN_STATUS.NEEDS_CORRECTIONS,
  TAX_RETURN_STATUS.READY_FOR_SIGNATURE,
  TAX_RETURN_STATUS.WAITING_FOR_PAYMENT,
  TAX_RETURN_STATUS.READY_FOR_EFILE,
  TAX_RETURN_STATUS.E_FILED,
  TAX_RETURN_STATUS.COMPLETED,
  TAX_RETURN_STATUS.INACTIVE,
] as const;

// ---------------------------------------------------------------------------
// Inquiry Status
// ---------------------------------------------------------------------------

export const INQUIRY_STATUS = {
  NEW: 'new',
  IN_PROGRESS: 'in_progress',
  WAITING_FOR_CLIENT: 'waiting_for_client',
  RESOLVED: 'resolved',
  CLOSED: 'closed',
} as const;

// ---------------------------------------------------------------------------
// Drake Match Status
// ---------------------------------------------------------------------------

export const DRAKE_MATCH_STATUS = {
  PENDING_REVIEW: 'pending_review',
  MATCHED: 'matched',
  MULTIPLE_MATCHES: 'multiple_matches',
  NO_MATCH: 'no_match',
} as const;

// ---------------------------------------------------------------------------
// E-File Status
// ---------------------------------------------------------------------------

export const EFILE_STATUS = {
  PENDING: 'pending',
  SUBMITTED: 'submitted',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
} as const;

// ---------------------------------------------------------------------------
// Signature Status
// ---------------------------------------------------------------------------

export const SIGNATURE_STATUS = {
  PENDING: 'pending',
  SENT: 'sent',
  SIGNED: 'signed',
} as const;

// ---------------------------------------------------------------------------
// Invoice Status (per-return invoice status on InvoiceInfo)
// ---------------------------------------------------------------------------

export const INVOICE_STATUS = {
  NOT_GENERATED: 'not_generated',
  GENERATED: 'generated',
  PAID: 'paid',
} as const;

// ---------------------------------------------------------------------------
// App Configuration
// ---------------------------------------------------------------------------

/** Default timeout in milliseconds for API requests (used by fetchApi). */
export const DEFAULT_API_TIMEOUT_MS = 30_000;

/** Default tax year used when no explicit year is provided. */
export const DEFAULT_TAX_YEAR = new Date().getFullYear();

/** Pagination defaults used across data tables and list views. */
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 25,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
} as const;

/** Default max results for Dataverse search calls. */
export const DEFAULT_SEARCH_MAX_RESULTS = 20;

// ---------------------------------------------------------------------------
// Review Status
// ---------------------------------------------------------------------------

export const REVIEW_STATUS = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  COMPLETED_PENDING_PAYMENT: 'completed_pending_payment',
  CHANGES_REQUIRED_PREPARER: 'changes_required_preparer',
  CHANGES_REQUIRED_TAXPAYER: 'changes_required_taxpayer',
} as const;

export const REVIEW_STATUS_DISPLAY_NAMES: Record<string, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  completed: 'Completed',
  completed_pending_payment: 'Completed - Pending Payment',
  changes_required_preparer: 'Changes Required (Preparer)',
  changes_required_taxpayer: 'Changes Required (Taxpayer)',
};

export const REVIEW_STATUS_COLORS: Record<string, string> = {
  not_started: '#6B7280',
  in_progress: '#3B82F6',
  completed: '#10B981',
  completed_pending_payment: '#F59E0B',
  changes_required_preparer: '#F97316',
  changes_required_taxpayer: '#EF4444',
};
